import React from "react";
import { Box, Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import PickupLocation from "./PickupLocation";
import { useForm } from "react-hook-form";
import { FormProvider } from "../../common/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup"
import DropLocation from "./DropLocation";
import PackageDetails from "./PackageDetails";
import OtherDetails from "./OtherDetails";
import { apiConstants, postGeneric, postMultipart } from "../../common/constants";
import { formatDate } from "../../helpers/UtilityHelper";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../common/PageTitle";
import PageHero from "../../common/PageHero";
import { useSnackbar } from "notistack";
import { getMerchant } from "../../helpers/ReduxHelpers";
import { FONT, SIZE, WEIGHT } from "../../styles/foundation/typography";
import { NAVY, BORDER, WHITE, PRIMARY_BLUE, SUCCESS } from "../../styles/foundation/colors";
import { SHADOW } from "../../styles/foundation/effects";
import { pageContainerSx } from "../../styles/patterns";

const stepperSx = {
  '& .MuiStepLabel-label': { fontFamily: FONT, fontSize: SIZE.xs, color: NAVY },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: WEIGHT.bold, color: NAVY },
  '& .MuiStepIcon-root.Mui-active': { color: PRIMARY_BLUE },
  '& .MuiStepIcon-root.Mui-completed': { color: SUCCESS },
};
const stepperCardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: 2.5 };
const formCardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: { xs: 2.5, md: 3.5 } };
const stepTitleSx = { fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.lg, color: NAVY, mb: 3 };

const steps = [
  "Pickup Details",
  "Delivery Details",
  "Box Details",
  "Other Details",
];

const INVOICE_VALUE_FOR_EWAY_BILL = 50000;

function CreatePTLShipment() {

  const [activeStep, setActiveStep] = React.useState(0);
  const navigate = useNavigate();
  const [creatingOrder, setIsCreatingOrder] = React.useState(false);
  // Document files + receiver contacts captured in Step 4. These are NOT part of the
  // create payload — they're uploaded against the AWB returned by the (synchronous)
  // create response, reusing the existing /additional_documents/upload endpoint.
  const [extraDocs, setExtraDocs] = React.useState({
    invoice: null,
    eway_bill: null,
    altContact1: '',
    altContact2: '',
  });
  // Gate document-upload errors so they appear only after a submit attempt.
  const [showDocErrors, setShowDocErrors] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const merchant = getMerchant();
  const merchantId = merchant.id ? merchant.id : ''

  const methods = useForm({
    defaultValues: {
      // invoice_date: new Date(),
      riskType: 'OWNER',
      payment_mode: 'PREPAID',
      insured: false,
      appointment_delivery: false,
      invoice_value: 0,
      reference: '',
      invoice_number: '',
      ewaybill: '',
      ewb_exp_date: null,
      invoice_date: null,
    },
    resolver: yupResolver(
      Yup.object().shape({
        warehouse_name: Yup.string().required("Pickup Location is Required!"),
        delivery_location: Yup.string().required("Delivery Location is Required!"),
        delivery_details: Yup.object().shape({
          to_name: Yup.string().required("Customer Name is Required!"),
          to_phone_number: Yup.string(),
          to_address: Yup.string().required("Customer Address is Required!"),
          to_email: Yup.string(),
          to_pincode: Yup.string().required("Address Pincode is Required!"),
          to_city: Yup.string().required("Address City is Required!"),
          to_state: Yup.string().required("Address State is Required!"),
          gstin: Yup.string(),
        }),
        item_list: Yup.array(Yup.object().shape({
          item_name: Yup.string().required(""),
          units: Yup.number().required("").min(1, ""),
          additional: Yup.object().shape({
            length: Yup.number().required("").min(1, ""),
            breadth: Yup.number().required("").min(1, ""),
            height: Yup.number().required("").min(1, ""),
            weight: Yup.number().required("").min(1, "")
          })
        })),
        invoice_value: Yup.number().required("").min(1, ""),
        selling_price: Yup.number().required("").min(1, ""),
        invoice_date: Yup.date().typeError("Please select a valid invoice date").required("Invoice date is a mandatory field!"),
        payment_mode: Yup.string().required(""),
        cod_amount: Yup.number().when('payment_mode', {
          is: val => val === 'COD',
          then: Yup.number().required(""),
        }).min(1, ""),
        reference: Yup.string().required(""),
        insured: Yup.boolean().required(""),
        appointment_delivery: Yup.boolean().required(""),
        invoice_number: Yup.string().required(""),
        ewaybill: Yup.string().when("invoice_value", {
          is: val => val > INVOICE_VALUE_FOR_EWAY_BILL,
          then: Yup.string().required("Eway bill number is required for invoices > Rs. 50,000")
        }),
        courier_id: Yup.string().when(["invoice_value", "ewaybill"], {
          is: (invoice_value, ewaybill) => invoice_value > INVOICE_VALUE_FOR_EWAY_BILL && ewaybill,
          then: Yup.string().required("Courier Id is mandatory")
        }),
        ewb_exp_date: Yup.date().nullable().when("invoice_value", {
          is: val => val > INVOICE_VALUE_FOR_EWAY_BILL,
          then: Yup.date()
            .nullable()
            .required("Eway bill expiry date is required for invoices > Rs. 50,000")
            .min(new Date(new Date().setHours(0, 0, 0, 0)), "Eway bill expiry date cannot be in the past"),
        }),
        riskType: Yup.string().required(""),
      })
    ),
  });

  const { handleSubmit } = methods;


  const onSubmit = (values) => {
    // Document files live outside RHF, so enforce their requirements here.
    setShowDocErrors(true);
    if (!extraDocs.invoice) {
      enqueueSnackbar("Invoice copy is required", { variant: "error" });
      return;
    }
    if (Number(values.invoice_value) > INVOICE_VALUE_FOR_EWAY_BILL && !extraDocs.eway_bill) {
      enqueueSnackbar("E-Way Bill copy is required for invoices > Rs. 50,000", { variant: "error" });
      return;
    }
    setIsCreatingOrder(true)
    const formData = JSON.parse(JSON.stringify(values));
    const request = {
      ...formData,
      order_type: 'Forward Shipment',
      skipRule: false,
      merchantOId: merchantId
    }

    if ((request.courier_id === "Bluedart_PRZ_5" || request.courier_id === "B2B_MOVIN_INT") && request.pickup_request_date) {
      request.pickup_request_date = formatDate(request.pickup_request_date, "yyyy-MM-dd hh:mm:ss")
    } else {
      delete request.pickup_request_date;
    }

    if (request.payment_mode === 'PREPAID') {
      request.cod_amount = 0;
    }

    if (request.invoice_date) {
      request.invoice_date = formatDate(request.invoice_date, "yyyy-MM-dd hh:mm:ss")
    }

    if (request.ewb_exp_date) {
      request.ewb_exp_date = formatDate(request.ewb_exp_date, "yyyy-MM-dd hh:mm:ss")
    }

    request.item_list.forEach((item) => {
      let deadWeight = item.additional.weight;
      let volWeight = (item.additional.length * item.additional.breadth * item.additional.height) / 4500;
      item.additional.weight = Math.max(deadWeight, volWeight) * 1000;
    })

    if (request.courier_id) {
      request.skipRule = true;
      request.courier_id = {
        prozoId: request.courier_id
      }
    }
    // return false;
    postGeneric(apiConstants.ASYNC_B2B_CREATE_ORDER, request)
      .then(async (response) => {
        // Sync create returns the full MongoOrder; AWB lives at result.awb_number.
        const awb = response?.data?.result?.awb_number;
        const { invoice, eway_bill, altContact1, altContact2 } = extraDocs;
        const hasExtras = invoice || eway_bill || altContact1 || altContact2;

        if (hasExtras) {
          if (!awb) {
            // Defensive: should not happen in sync mode, but never block the success.
            enqueueSnackbar(
              'Order created, but no AWB was returned so documents/contacts could not be attached. Add them from the shipments list via "Add Documents".',
              { variant: 'warning' }
            );
            navigate("/b2b-orders");
            return;
          }
          try {
            const fd = new FormData();
            if (invoice) fd.append('invoice', invoice);
            if (eway_bill) fd.append('eway_bill', eway_bill);
            const data = { awbNumber: awb };
            if (altContact1) data.altContact1 = altContact1;
            if (altContact2) data.altContact2 = altContact2;
            fd.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
            await postMultipart(apiConstants.ADDITIONAL_DOCUMENTS_UPLOAD, fd);
          } catch (e) {
            // Order already exists — degrade gracefully, don't surface as a create failure.
            const detail = e?.response?.data?.message || e?.message;
            enqueueSnackbar(
              `Order created, but uploading documents/contacts failed${detail ? `: ${detail}` : ''}. You can add them from the shipments list via "Add Documents".`,
              { variant: 'warning' }
            );
            navigate("/b2b-orders");
            return;
          }
        }

        enqueueSnackbar("Successfully Created!", { variant: 'success' });
        navigate("/b2b-orders");
      }).catch((err) => {
        const message = err?.response?.data?.message || err.message;
        enqueueSnackbar(message || "Error creating order", { variant: "error" });
      }).finally(() => {
        setIsCreatingOrder(false)
      })
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Create PTL Shipment</PageTitle>
      {/* <PageHero title='Create Non-ABD Shipment' subtitle='Book a new PTL (part-truck-load) B2B shipment' sx={{ mb: 2.5 }} /> */}
      <Grid container spacing={2.5} sx={{ alignItems: 'flex-start' }}>
        <Grid item xs={12} md={3}>
          <Box sx={stepperCardSx}>
            <Stepper orientation="vertical" activeStep={activeStep} sx={stepperSx}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          <Box sx={formCardSx}>
            <Typography sx={stepTitleSx}>{activeStep + 1}. {steps[activeStep]}</Typography>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, () => setShowDocErrors(true))}>
              {activeStep === 0 && (
                <PickupLocation setActiveStep={setActiveStep} />
              )}
              {activeStep === 1 && (
                <DropLocation setActiveStep={setActiveStep} />
              )}
              {activeStep === 2 && (
                <PackageDetails setActiveStep={setActiveStep} />
              )}
              {activeStep === 3 && (
                <OtherDetails creatingOrder={creatingOrder} setActiveStep={setActiveStep} extraDocs={extraDocs} setExtraDocs={setExtraDocs} showDocErrors={showDocErrors} />
              )}
            </FormProvider>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default CreatePTLShipment;